import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const FONTS_SOURCE = 'https://www.w3.org/TR/css-fonts-4/#textmetrics';
const HARFBUZZ_SOURCE = 'https://harfbuzz.github.io/';

/**
 * OS font engine sub-pixel shaping metrics (novel_bot_detection §2C).
 *
 * Canvas noise defeats image hashing, but TextMetrics still says something
 * about the text stack. What this probe ACTUALLY does is measure the width-per-
 * em ratio of a few scripts at fractional sizes, plus a light font-availability
 * check, and bucket the result: a container with a minimal font set often falls
 * back to default widths that differ from a real desktop's font stack.
 *
 * We deliberately do NOT claim to compare actualBoundingBox values across
 * DirectWrite/CoreText/FreeType — an earlier revision did, but the
 * classification has always run on width-per-em ratios, so that text was
 * wrong. The bounding-box values are still recorded as signals for a reader to
 * inspect; they do not drive the verdict. The result is a coarse profile, not
 * an engine identification.
 */
const SCRIPTS: Array<{ key: string; text: string; label: string }> = [
  { key: 'latin', text: 'Handgloves AV Wy', label: 'Latin' },
  { key: 'arabic', text: 'مرحبا بالعالم', label: 'Arabic (RTL + ligatures)' },
  { key: 'devanagari', text: 'हिन्दी समाचार', label: 'Devanagari conjuncts' },
  { key: 'thai', text: 'ภาษาไทยทดสอบ', label: 'Thai' },
  { key: 'cjk', text: '中文测试字体', label: 'CJK ideographs' },
  { key: 'hangul', text: '한국어 테스트', label: 'Hangul' },
];

// Fractional sizes chosen to force sub-pixel positioning in the layout engine.
const SIZES = [12.35, 15.75, 18.2];

/** A per-script snapshot of the metrics at each fractional size. */
interface ScriptMetrics {
  key: string;
  label: string;
  samples: Array<{ size: number; width: number; ascent: number; descent: number; left: number; right: number }>;
}

export const fontsProbe: Probe = {
  id: 'fonts',
  label: 'Font shaping',
  layer: 'engine',
  tier: 'client',
  category: 'hardware',
  description: 'TextMetrics width-per-em ratios across scripts and fractional sizes',
  budgetMs: 2000,

  async run(): Promise<ProbeResult> {
    const ctx = document.createElement('canvas');
    const g = ctx.getContext('2d');
    if (!g) {
      return { signals: { available: na<boolean>('unavailable', 'client', 'no 2d context') } };
    }

    const findings: Finding[] = [];
    const metrics: ScriptMetrics[] = [];

    for (const script of SCRIPTS) {
      const samples: ScriptMetrics['samples'] = [];
      for (const size of SIZES) {
        g.font = `${size}px sans-serif`;
        const m = g.measureText(script.text);
        samples.push({
          size,
          width: sig(m.width, 4) ?? 0,
          ascent: sig(m.actualBoundingBoxAscent, 4) ?? 0,
          descent: sig(m.actualBoundingBoxDescent, 4) ?? 0,
          left: sig(m.actualBoundingBoxLeft, 4) ?? 0,
          right: sig(m.actualBoundingBoxRight, 4) ?? 0,
        });
      }
      metrics.push({ key: script.key, label: script.label, samples });
    }

    // Availability of a few well-known fonts (installable font checks are a
    // classic, but shallow, fingerprint; kept light here).
    const baseFont = '72px monospace';
    const testFonts = ['monospace', 'sans-serif', 'serif', 'Arial', 'Times New Roman', 'Courier New'];
    const fontAvailability: Record<string, boolean> = {};
    for (const f of testFonts) {
      g.font = `${baseFont.includes(f) ? '' : baseFont.replace('monospace', f)}`;
      // A font is "available" if measuring it differs from a nonsense baseline.
      g.font = `72px ${f}, monospace`;
      fontAvailability[f] = widthOf(g, 'mmmmmmmmmmlli') !== widthOf(g, '  ');
    }

    // --- Shaper classification --------------------------------------------
    // We compute a coarse profile from the width-per-em ratio of the CJK and
    // Arabic samples and compare it to the claimed OS. The bucket is coarse:
    // "Linux/Unix" really means "minimal/default-width font stack".
    const shaper = classifyShaper(metrics);
    const claimedOs = detectClaimedOs();
    const shaperOs = shaper.impliedOs;

    // A mismatch (claimed Windows UA but metrics look FreeType) is the strong
    // signal this probe exists for.
    const osConflict =
      (claimedOs === 'Windows' && shaperOs === 'Linux/Unix') ||
      (claimedOs === 'macOS' && shaperOs === 'Linux/Unix');

    if (osConflict) {
      findings.push({
        id: 'fonts.shaper-os-mismatch',
        severity: 'medium',
        category: 'hardware',
        layer: 'engine',
        confidence: 'low',
        title: 'Font metrics match a minimal/default-width font stack',
        detail:
          'The width-per-em ratios for complex scripts match the default-width fallback profile typical of a minimal font set, while the User-Agent claims a desktop OS that normally ships a fuller set. A stripped container is one explanation, and a freshly provisioned or font-limited machine is another; the profile buckets are coarse. Read this as supporting evidence, not proof of spoofing.',
        sources: [FONTS_SOURCE, HARFBUZZ_SOURCE],
        evidence: [`claimedOs=${claimedOs}`, `metricsImply=${shaperOs}`, `profile=${shaper.profile}`],
        scoreContribution: 10,
      });
    }

    return {
      signals: {
        available: ok(true, 'client'),
        metrics: ok(metrics, 'client'),
        fontAvailability: ok(fontAvailability, 'client'),
        shaperProfile: ok(shaper.profile, 'derived'),
        shaperImpliedOs: ok(shaperOs, 'derived'),
        claimedOs: ok(claimedOs, 'derived'),
        osConflict: ok(osConflict, 'derived'),
      },
      findings,
      parts: {
        fontShaping: {
          // Hash the shaping profile and a coarse width-per-em ratio for the
          // complex scripts, not the exact float metrics (which jitter).
          profile: shaper.profile,
          arabicRatio: shaper.arabicRatio,
          devanagariRatio: shaper.devanagariRatio,
        },
      },
    };
  },
};

function widthOf(g: CanvasRenderingContext2D, text: string): number {
  return g.measureText(text).width;
}

function detectClaimedOs(): string {
  const ua = navigator.userAgent;
  if (/Windows NT/i.test(ua)) return 'Windows';
  if (/Android/i.test(ua)) return 'Android';
  if (/(iPhone|iPad|iPod)/i.test(ua)) return 'iOS';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
  if (/CrOS/i.test(ua)) return 'ChromeOS';
  if (/Linux/i.test(ua)) return 'Linux/Unix';
  return 'unknown';
}

/**
 * Classify the shaping engine from measured metrics.
 *
 * We keep this deliberately coarse. The absolute metrics depend heavily on the
 * available fonts in the container, so a "profile" is more robust than an exact
 * per-engine match: we bucket by the width-per-em ratio of the CJK and Arabic
 * samples, which is dominated by the shaper's default advance behaviour.
 */
function classifyShaper(metrics: ScriptMetrics[]): {
  profile: string;
  impliedOs: string;
  arabicRatio: number | null;
  devanagariRatio: number | null;
} {
  const byKey = new Map(metrics.map((m) => [m.key, m]));

  // width per em size (use the first sample size).
  const ratio = (key: string): number | null => {
    const m = byKey.get(key);
    if (!m || m.samples.length === 0) return null;
    const s = m.samples[0];
    if (!s.size) return null;
    return sig(s.width / s.size, 2);
  };

  const arabicRatio = ratio('arabic');
  const devanagariRatio = ratio('devanagari');
  const cjkRatio = ratio('cjk');

  // Profile buckets, chosen from observed spread rather than exact engine
  // constants, and labelled generically so we do not over-claim.
  let profile = 'generic';
  if (cjkRatio != null) {
    if (cjkRatio > 0.95 && cjkRatio < 1.05) profile = 'cjk-fullwidth';
    else if (cjkRatio >= 1.05) profile = 'cjk-wide';
    else profile = 'cjk-narrow';
  }

  // OS inference: this is the weak part. A container with a minimal font set
  // often renders complex scripts with a fallback and produces a distinctive
  // "everything at default width" profile. We only claim Linux/Unix when the
  // profile is clearly the fallback one AND the claimed OS is not already
  // Linux/Unix.
  let impliedOs = 'unknown';
  if (profile === 'cjk-narrow' && arabicRatio != null && arabicRatio < 0.6) {
    impliedOs = 'Linux/Unix';
  }

  return { profile, impliedOs, arabicRatio, devanagariRatio };
}

registerProbe(fontsProbe);
